import { designVariables } from "@studio3t/design-variables";
import {
  ButtonMediumPrimary,
  ButtonMediumSecondary,
  GenericInput,
  InputLabelOrientation,
  InputState,
  type InputValidationState,
} from "@studio3t/generic-components";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useState,
} from "react";
import styled from "styled-components";
import {
  ModalBackdrop,
  ModalCard,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalTitle,
} from "@/components/ModalChrome";
import { ModalCloseButton } from "@/components/ModalCloseButton";
import { useModalFocus } from "@/hooks/useModalFocus";

const MODAL = designVariables.ui.modal;

// The submit-time verdict on a typed name: empty and duplicate checks in
// one place, null when the name is fine.
function nameProblem(
  trimmed: string,
  emptyMessage: string,
  validateName?: (trimmedName: string) => InputValidationState | null,
): InputValidationState | null {
  if (trimmed === "") {
    return { state: InputState.Error, messages: [emptyMessage] };
  }
  return validateName?.(trimmed) ?? null;
}

// All of the modal's field state and behavior: fresh-per-open reset,
// Escape-to-close, and the validate-save-close submit flow. The component
// below stays purely presentational.
function useNameField(options: {
  isOpen: boolean;
  initialName: string;
  emptyMessage: string;
  errorFallback: string;
  validateName?: (trimmedName: string) => InputValidationState | null;
  onSave: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const { isOpen, initialName, onClose } = options;
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<InputValidationState | null>(null);

  // Fresh field per open.
  useEffect(() => {
    if (!isOpen) return;
    setName(initialName);
    setProblem(null);
  }, [isOpen, initialName]);

  const close = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = name.trim();
    const invalid = nameProblem(
      trimmed,
      options.emptyMessage,
      options.validateName,
    );
    if (invalid) {
      setProblem(invalid);
      return;
    }
    setSaving(true);
    try {
      await options.onSave(trimmed);
      onClose();
    } catch (err) {
      setProblem({
        state: InputState.Error,
        messages: [err instanceof Error ? err.message : options.errorFallback],
      });
    } finally {
      setSaving(false);
    }
  };

  const edit = (value: string) => {
    setName(value);
    setProblem(null);
  };

  return { name, edit, problem, saving, close, submit };
}

// Single-Name-field modal (Figma Modal demo 13376-42327 / Save schema
// 14502-134872 / Save as draft 15949-170609): header with the H4 title and
// close, a Name field (label stacked above the underline input), footer
// with the primary action then Cancel. Esc and the backdrop close it, Enter
// in the field submits. `validateName` runs on the trimmed name before
// saving (e.g. the workspace duplicate check); a thrown onSave error is
// shown on the field.
export function NameModal({
  isOpen,
  onClose,
  onSave,
  title,
  submitLabel = "Save",
  initialName = "",
  intro,
  label = "Name",
  placeholder,
  emptyMessage,
  validateName,
  testIdBase,
  errorFallback,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Performs the save; a thrown error is shown on the field. */
  onSave: (name: string) => Promise<void>;
  title: string;
  submitLabel?: string;
  initialName?: string;
  /** Copy above the field — the duplicate modal's info alert (Figma
   *  15013-126334). */
  intro?: ReactNode;
  /** The field's label — "Name" unless the modal names a specific thing
   *  (the schema save's "Schema name"). */
  label?: string;
  placeholder: string;
  /** Field error shown when submitting an empty name. */
  emptyMessage: string;
  validateName?: (trimmedName: string) => InputValidationState | null;
  /** data-testid prefix: `${base}-modal`, `${base}-confirm`. */
  testIdBase: string;
  /** Field error when onSave throws a non-Error. */
  errorFallback: string;
}) {
  // Focus returns to the trigger on close; the field's autoFocus holds it
  // while open.
  const modalRef = useModalFocus(isOpen);
  const titleId = useId();
  const { name, edit, problem, saving, close, submit } = useNameField({
    isOpen,
    initialName,
    emptyMessage,
    errorFallback,
    validateName,
    onSave,
    onClose,
  });

  // A lone text field labeled "Name" in a dialog reads as an identity
  // form to password managers. The opt-outs must be ON the element the
  // moment it enters the DOM (scanners run from a MutationObserver
  // microtask — useEffect is too late), so they're set in the memoized
  // ref callback, synchronous with the commit; GenericInput doesn't
  // forward unknown props, hence setAttribute. NOTE these are best
  // effort: an extension set to "show on field focus" decorates every
  // field on every site and cannot be suppressed from the page.
  const optOutInputRef = useCallback((input: HTMLInputElement | null) => {
    if (!input) return;
    input.setAttribute("autocomplete", "off");
    input.setAttribute("data-1p-ignore", "true");
    input.setAttribute("data-lpignore", "true");
    input.setAttribute("data-bwignore", "true");
    // "suite" in the surrounding copy is an ADDRESS keyword (Apt/Suite/
    // Unit) — next to the "Name" label it makes identity autofill
    // classify the dialog as an address form. The name attribute
    // outweighs nearby text in the classifiers, so it declares the field
    // something no manager fills.
    input.setAttribute("name", "search-query");
  }, []);

  if (!isOpen) return null;

  return (
    <ModalBackdrop onClick={close} role="presentation">
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={`${testIdBase}-modal`}
      >
        <ModalHeader>
          <ModalTitle id={titleId}>{title}</ModalTitle>
          <ModalCloseButton
            $top={MODAL.header.paddingh}
            $right={MODAL.paddingw}
            onClick={close}
            disabled={saving}
            aria-label="Close"
          />
        </ModalHeader>
        {/* autoComplete off on the FORM too (the browser's autofill keys
            off the form, and Chrome can ignore the input's own attribute),
            and the password-manager opt-outs as well — 1Password consults
            ancestors when classifying a field. */}
        <Body
          onSubmit={submit}
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
        >
          {intro}
          <GenericInput
            ref={optOutInputRef}
            label={{ text: label, orientation: InputLabelOrientation.Top }}
            placeholder={placeholder}
            value={name}
            onChange={(e) => edit(e.target.value)}
            state={problem ?? undefined}
            disabled={saving}
            autoFocus
          />
          {/* Hidden submit so Enter in the field saves. */}
          <HiddenSubmit type="submit" aria-hidden tabIndex={-1} />
        </Body>
        <ModalFooter>
          {/* macOS button order — Cancel left of the accepting action.
              (The Figma modal demo showed Save-then-Cancel; overridden on
              request, 2026-07-29.) */}
          <ModalFooterActions>
            <ButtonMediumSecondary
              text="Cancel"
              onClick={close}
              disabled={saving}
            />
            <ButtonMediumPrimary
              text={saving ? "Saving…" : submitLabel}
              onClick={() => submit()}
              disabled={saving}
              data-testid={`${testIdBase}-confirm`}
            />
          </ModalFooterActions>
        </ModalFooter>
      </Modal>
    </ModalBackdrop>
  );
}

// Figma Modal (demo) 15013-126334: 600px body.
const Modal = styled(ModalCard)`
  width: 600px;
`;

// Figma 15013-126334: an intro alert sits 10px above the field.
const Body = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 ${MODAL.paddingw}px ${MODAL.paddingb}px;
`;

const HiddenSubmit = styled.button`
  display: none;
`;
